import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  FieldErrors,
  FormError,
  NewShipmentInputData,
} from "../../../redux/slices/Types";
import LynksTabs from "../../common/LynksTabs";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AssetStatus,
  GetCustomerDetailsQuery,
  ShipmentLocationType,
  useGetGoodProfileListQuery,
} from "../../../graphql/generated";
import { ExpandMore, RemoveCircle } from "@mui/icons-material";
import ReceiverFormContainer from "./ReceiverForm";
import LabeledAddButton from "../../common/LabeledAddButton";
import { useDispatch } from "react-redux";
import {
  addReceiver,
  removeShipperOrReceiver,
  setGoodProfiles,
} from "../../../redux/slices/shipment/ShipmentForm.slice";
import {
  hideDialog,
  showDialog,
} from "../../../redux/slices/alert/Alert.slice";
import ShipmentReferencesForm from "./ShipmentReferencesForm/ShipmentReferencesForm";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import TrailerRequirementsFormContainer from "./TrailerRequirementsForm/TrailerRequirementsFormContainer";
import { isDropoffLocation } from "../../../utils/location/isPickupLocation";
import ShipmentRecurrenceForm from "./ShipmentRecurrenceForm/ShipmentRecurrenceForm";
import { testIds } from "./CommodityDrivenShipmentForm.testIds";

type CommodityDrivenShipmentFormProps = {
  shipmentInput: NewShipmentInputData;
  //   shipmentDetails: ShipmentDetailsProps["shipment"] | null;
  errors?: FieldErrors;
  shipmentLocationErrors?: FormError[];
  loading: boolean;
  validating: boolean;
  onUpdate: (updates: Partial<NewShipmentInputData>) => void;
  onChange: (name: string, value: string | null | undefined) => void;
  onValidate: () => Promise<boolean>;
  onSubmit: () => Promise<void>;
  isChildLoad?: boolean;
  isSplit?: boolean;
  isEdit?: boolean;
  customerDetails?: GetCustomerDetailsQuery["customerById"];
};

enum ShipmentFormTabs {
  Shipment = "shipment",
  References = "references",
}

const CommodityDrivenShipmentForm = (
  props: CommodityDrivenShipmentFormProps
) => {
  const { t } = useTranslation(["orders", "common"]);
  const [quantityError, setQuantityError] = useState<string | null>(null);
  const [receivedGoodsError, setReceivedGoodsError] = useState<string | null>(null);

  const tabs = [
    {
      label: t("orders:shipment", "Shipment"),
      value: ShipmentFormTabs.Shipment,
      testId: testIds.commodityDrivenShipmentFormShipmentDetails,
    },
    {
      label: t("orders:references", "References"),
      value: ShipmentFormTabs.References,
      testId: testIds.commodityDrivenShipmentFormReferences,
    },
  ];

  const [activeTab, setActiveTab] = useState(ShipmentFormTabs.Shipment);
  const receiverLocations =
    props.shipmentInput.shipmentLocations.filter(isDropoffLocation);

  const goodProfilesQuery = useGetGoodProfileListQuery({
    includeInactive: !!props.isEdit,
  });

  const existingCommodityIds = useMemo(
    () =>
      new Set(
        props.shipmentInput.shipmentLocations.flatMap((location) =>
          (location.receivedGoods || [])
            .map((good) => good.goodProfileId)
            .filter(Boolean)
            .map(String)
        )
      ),
    [props.shipmentInput.shipmentLocations]
  );

  const goodProfiles = useMemo(
    () =>
      (goodProfilesQuery.data?.goodProfiles.data || []).filter(
        (profile) =>
          profile.status !== AssetStatus.Inactive ||
          existingCommodityIds.has(profile._id)
      ),
    [goodProfilesQuery.data, existingCommodityIds]
  );

  const dispatch = useDispatch();

  const handleAddReceiverClick = () => {
    dispatch(addReceiver());
  };

  useEffect(() => {
    loadGoodProfiles();

    async function loadGoodProfiles() {
      const goodProfilesResponse = await useGetGoodProfileListQuery.fetcher({
        includeInactive: !!props.isEdit,
      })();

      const goodProfiles = (goodProfilesResponse.goodProfiles.data || []).filter(
        (profile) =>
          profile.status !== AssetStatus.Inactive ||
          existingCommodityIds.has(profile._id)
      );

      dispatch(setGoodProfiles(goodProfiles));
    }
  }, [dispatch, existingCommodityIds, props.isEdit]);

  const handleRemoveShipperOrReceiver = (type: string, id: string) => {
    dispatch(
      showDialog({
        title: t("common:error.dangerZone", "Danger zone"),
        description: t(
          `error.deleteShipperReceiverConfirmation.${type}`,
          `Do you really want to delete this ${type} ? This action cannot be undone.`
        ),
        type: "error",
        actions: [
          {
            type: "error",
            title: t("common:error.yesDelete", "Yes, Delete"),
            onClick: async () => {
              dispatch(removeShipperOrReceiver(id));
              dispatch(hideDialog());
            },
          },
          {
            type: "primary",
            title: t("common:error.noCancel", "No, Cancel"),
          },
        ],
      })
    );
  };

  const handleSelectTab = useCallback((tab: string) => {
    setActiveTab(tab as ShipmentFormTabs);
  }, []);

  const goodProfilesShippedGoods = useMemo(
    () =>
      goodProfiles.map((goodProfile) => ({
        _id: goodProfile._id,
        label: goodProfile.label,
        quantity: Infinity,
        goodProfileId: goodProfile._id,
        status: goodProfile.status,
        unit: goodProfile.unit,
        weight: goodProfile.weight,
      })),
    [goodProfiles]
  );

  return (
    <Container
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <LynksTabs tabs={tabs} value={activeTab} onSelectTab={handleSelectTab} />

      <Stack justifyContent="space-between" flex={1}>
        <Box
          display={activeTab === ShipmentFormTabs.Shipment ? undefined : "none"}
        >
          <Accordion
            sx={{
              mb: 2,
            }}
          >
            <AccordionSummary
              data-testid={
                testIds.commodityDrivenShipmentFormTrailerRequirementsAccordionSummary
              }
              expandIcon={<ExpandMore />}
            >
              <Typography>Trailer Requirements</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TrailerRequirementsFormContainer
                shipment={props.shipmentInput}
                onUpdate={props.onUpdate}
                errors={props.errors}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            sx={{
              mb: 2,
            }}
          >
            <AccordionSummary
              data-testid={
                testIds.commodityDrivenShipmentFormRecurrenceAccordionSummary
              }
              expandIcon={<ExpandMore />}
            >
              <Typography>Recurrence</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ShipmentRecurrenceForm
                shipment={props.shipmentInput}
                onUpdate={props.onUpdate}
                isChildLoad={props.isChildLoad}
                validationResult={null}
              />
            </AccordionDetails>
          </Accordion>
          {props.isSplit ? (
            <Grid xs={12} sx={{ mt: 2 }}>
              <Alert severity="warning">
                {t(
                  "error.editLocationSplitLoadError",
                  "You cannot edit locations on a split load"
                )}
              </Alert>
            </Grid>
          ) : null}
          <Stack spacing={2} position="relative">
            {props.isSplit ? (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  backgroundColor: "white",
                  opacity: 0.5,
                  zIndex: 2000,
                }}
              />
            ) : null}
            {receiverLocations.map((receiverLocation, index) => (
              <Box
                key={`${receiverLocation._id}-${index}`}
                sx={{ position: "relative" }}
              >
                {receiverLocations.length > 1 && (
                  <IconButton data-testid={`${testIds.commodityDrivenShipmentFormRemoveReceiver}-${receiverLocation._id}`}
                    sx={{ position: "absolute", right: -30, top: -30 }}
                    color="default"
                    onClick={() =>
                      handleRemoveShipperOrReceiver(
                        "receiver",
                        receiverLocation._id
                      )
                    }
                  >
                    <RemoveCircle />
                  </IconButton>
                )}
                <ReceiverFormContainer
                  shippedGoods={goodProfilesShippedGoods.filter(
                    (good) =>
                      good.status !== AssetStatus.Inactive ||
                      (receiverLocation.receivedGoods || []).some(
                        (receivedGood) =>
                          String(receivedGood.goodProfileId || "") ===
                          String(good.goodProfileId || "")
                      )
                  )}
                  receivedGoods={receiverLocation.receivedGoods}
                  key={index}
                  receiver={receiverLocation}
                  inline
                  errors={props.shipmentLocationErrors?.find(
                    (err) => err.formId === receiverLocation._id
                  )}
                  allowAddressLocation={false}
                />
                <Divider
                  sx={{
                    my: 3,
                  }}
                />
              </Box>
            ))}
            <LabeledAddButton data-testid={testIds.commodityDrivenShipmentFormAddReceiver}
              sx={{ height: 50, width: 20 }}
              onClick={handleAddReceiverClick}
              label={t("orders:addReceiver", "Add Receiver")}
            />
          </Stack>
        </Box>
        {activeTab === ShipmentFormTabs.References ? (
          <Box>
            <ShipmentReferencesForm
              shipment={props.shipmentInput}
              onUpdate={props.onUpdate}
              errors={props.errors}
            />
          </Box>
        ) : null}

        {quantityError ? (
          <Alert severity="error" sx={{ my: 2 }}>{quantityError}</Alert>
        ) : null}
        {receivedGoodsError ? (
          <Alert severity="error" sx={{ my: 2 }}>{receivedGoodsError}</Alert>
        ) : null}
        <Stack justifyContent="flex-end" direction="row">
          <LoadingButton data-testid={testIds.commodityDrivenShipmentFormSave}
            loading={props.validating || props.loading}
            onClick={async () => {
              const dropoffLocations =
                props.shipmentInput.shipmentLocations.filter(
                  (l) => l.locationType === ShipmentLocationType.DropOff
                );

                const hasInvalidReceiver = dropoffLocations.some(
                  (location) =>
                    !location.receiver || !location.receiver?.length);
                  
                if (hasInvalidReceiver) {
                  setReceivedGoodsError(
                    t(
                      "orders:error.commodityReceiverRequired",
                      "The receiver field is required."
                    )
                  );
                  return;
                }
                setReceivedGoodsError(null);

                let receiverWithNoCommoodities = null;
                const hasInvalidReceivedGoods = dropoffLocations.some(
                  (location) => {
                    if (!location.receivedGoods || !location.receivedGoods.length) {
                      receiverWithNoCommoodities = location.name;
                      return true;
                    }
                    return false;
                  }
                );
                if (hasInvalidReceivedGoods) {
                  setReceivedGoodsError(
                    t(
                      "orders:error.commodityReceivedGoodsRequired",
                      `At least one commodity should be selected${
                        receiverWithNoCommoodities
                          ? ` for ${receiverWithNoCommoodities}`
                          : ""
                      }.`,
                    ),
                  );
                  return;
                }
                setReceivedGoodsError(null);
                
                const hasInvalidQuantity = dropoffLocations.some((location) =>
                location.receivedGoods.some(
                  (rg) =>
                    rg.quantity === null ||
                    rg.quantity === undefined ||
                    rg.quantity <= 0
                )
              );
              if (hasInvalidQuantity) {
                setQuantityError(
                  t(
                    "orders:error.commodityQuantityRequired",
                    "Commodity quantity must be greater than zero."
                  )
                );
                return;
              }
              setQuantityError(null);

              const hasReceivedGoodsWithInvalidShipper = dropoffLocations.some((location) =>
                location.receivedGoods.some(
                  (rg) => !rg.shipperId || !rg.shipperId?.length || rg.shipperId?.trim() === ""
                )
              );
              if (hasReceivedGoodsWithInvalidShipper) {
                setReceivedGoodsError(
                  t(
                    "orders:error.commodityShipperRequired",
                    "Each received good/commodity must have a valid shipper."
                  )
                );
                return;
              }
              setReceivedGoodsError(null); 

              if (await props.onValidate()) {
                await props.onSubmit();
              }
            }}
            variant="contained"
            size="large"
          >
            {t("common:save", "Save")}
          </LoadingButton>
        </Stack>
      </Stack>
    </Container>
  );
};

export default CommodityDrivenShipmentForm;
